import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../RestAPI";
import { showErrorToast } from "../../constants/ToastService.js";
import "../SharedTable.css";
import FilterDropdown from "../Common Components/FilterDropdown";
import useCurrentProject, { resolveProject } from "../Common Components/useCurrentProject";

const PAGE_SIZE = 15;

// Default view = Sprint Tickets (Story/Epic rows). Bugs can be toggled in
// as a separate group via the pill filter.
const TICKET_SCOPE = {
    sprint: { key: 'sprint', label: 'Sprint Tickets', match: (t) => t.ticket_type !== 'Bug' },
    bugs:   { key: 'bugs',   label: 'Bugs',           match: (t) => t.ticket_type === 'Bug' },
    all:    { key: 'all',    label: 'All',            match: ()  => true },
};

const OverallReport = () => {
    const [sprints, setSprints] = useState([]);
    const [reportsData, setReportData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [currentSprint, setCurrentSprint] = useState('');
    const [companyStatus, setCompanyStatus] = useState([]);
    const [totalTickets, setTotalTickets] = useState(0);
    const [statusCount, setStatusCount] = useState({});
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState('sprint');
    const [page, setPage] = useState(1);

    const getProjects = useCallback(() => {
        fetch(`${API_BASE_URL}core/project/`, { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } })
            .then(res => res.json()).then(data => { if (data.status === "success") { setProjects(data.data); setCurrentProject(prev => resolveProject(data.data, prev)); } else showErrorToast(data.message); })
            .catch(err => { console.error(err); showErrorToast("Failed to fetch projects"); });
    }, []);

    const getSprints = useCallback((project_id) => {
        fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${project_id}`, { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } })
            .then(res => res.json()).then(data => { if (data.status === "success" && Array.isArray(data.data)) { const sorted = data.data.sort((a, b) => new Date(b.end_date) - new Date(a.end_date)); setSprints(sorted); if (sorted?.length) setCurrentSprint(sorted[0]?.id); } else showErrorToast(data.message); })
            .catch(err => { console.error(err); showErrorToast("Failed to fetch sprints"); });
    }, []);

    const getReportData = useCallback(async (sprintId, filterStatus) => {
        try {
            const res = await fetch(`${API_BASE_URL}reports/qa/fails/detail/?sprint_id=${sprintId}&status=${filterStatus || ''}`, { method: "GET", headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } });
            const data = await res.json();
            if (data.status === "success") { setReportData(data.data); setCompanyStatus(data.company_status || []); setTotalTickets(data.total_count || 0); setStatusCount(data.status_count || {}); }
            else showErrorToast(data.message);
        } catch (err) { console.error(err); showErrorToast("Failed to fetch report"); }
    }, []);

    useEffect(() => { if (currentProject) getSprints(currentProject); else setSprints([]); }, [currentProject]);
    useEffect(() => { if (currentSprint) getReportData(currentSprint); else setReportData([]); }, [currentSprint]);
    useEffect(() => { getProjects(); }, [getProjects]);

    const filterByStatus = (statusValue) => {
        if (currentSprint && statusValue && statusValue !== 'Total Tickets') getReportData(currentSprint, statusValue);
    };

    const scopedData = useMemo(() => {
        const matcher = TICKET_SCOPE[scope]?.match || TICKET_SCOPE.sprint.match;
        return reportsData.filter(matcher);
    }, [reportsData, scope]);

    const counts = useMemo(() => ({
        sprint: reportsData.filter(TICKET_SCOPE.sprint.match).length,
        bugs:   reportsData.filter(TICKET_SCOPE.bugs.match).length,
        all:    reportsData.length,
    }), [reportsData]);

    const filtered = useMemo(() => {
        if (!search.trim()) return scopedData;
        const q = search.toLowerCase();
        return scopedData.filter(t =>
            (t.ticket_no || '').toLowerCase().includes(q)
            || (t.short_description || '').toLowerCase().includes(q)
            || (t.developer_names || '').toLowerCase().includes(q)
        );
    }, [scopedData, search]);

    const paginated = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

    useEffect(() => { setPage(1); }, [scope, currentSprint, currentProject]);

    return (
        <div style={{ padding: '20px 28px' }}>
            <div className="fd-selector-row">
                <FilterDropdown
                    label="Project"
                    value={currentProject}
                    options={projects || []}
                    onChange={setCurrentProject}
                    renderLabel={o => o?.project_name || ''}
                    getValue={o => String(o?.id)}
                />
                <FilterDropdown
                    label="Sprint"
                    value={currentSprint}
                    options={sprints || []}
                    onChange={setCurrentSprint}
                    renderLabel={o => o?.sprint_name || ''}
                    getValue={o => String(o?.id)}
                />
            </div>

            <div className="fd-card">
                <div className="fd-card-header">
                    <div className="fd-card-header-left">
                        <h1 className="fd-card-title">QA Fails Report</h1>
                        <p className="fd-card-subtitle">Track bug counts and status breakdowns per ticket</p>
                    </div>
                </div>

                {/* Summary chips */}
                <div className="fd-summary-row">
                    <div className="fd-summary-chip" onClick={() => {}}>
                        <span className="fd-summary-chip__value">{totalTickets}</span>
                        <span className="fd-summary-chip__label">Total Tickets</span>
                    </div>
                    {companyStatus.map(status => (
                        <div key={status} className="fd-summary-chip" onClick={() => filterByStatus(status)}>
                            <span className="fd-summary-chip__value">{statusCount[status] || 0}</span>
                            <span className="fd-summary-chip__label">{status}</span>
                        </div>
                    ))}
                </div>

                <div className="fd-toolbar">
                    <div className="fd-scope-pills">
                        {['sprint', 'bugs', 'all'].map(k => (
                            <button
                                key={k}
                                className={`fd-scope-pill ${scope === k ? 'fd-scope-pill--active' : ''}`}
                                onClick={() => setScope(k)}
                            >
                                {TICKET_SCOPE[k].label} <span className="fd-scope-pill__count">{counts[k]}</span>
                            </button>
                        ))}
                    </div>
                    <input className="fd-search" type="text" placeholder="Search tickets..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                    <span className="fd-record-count">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table className="fd-table">
                        <thead>
                            <tr>
                                <th style={{ width: 36 }}>#</th>
                                <th>Ticket No</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Developers</th>
                                <th>Total Bugs</th>
                                <th>Pending</th>
                                <th>Completed</th>
                                {companyStatus.map(s => <th key={s}>{s}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length > 0 ? paginated.map((t, i) => {
                                const tType = t.ticket_type || (t.bug_code ? 'Bug' : 'Story');
                                return (
                                    <tr key={t.id || i}>
                                        <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                                        <td>{t.ticket_no || '-'}</td>
                                        <td><span className={`fd-type-pill fd-type-pill--${tType.toLowerCase()}`}>{tType}</span></td>
                                        <td>{t.short_description || '-'}</td>
                                        <td>{t.sts || '-'}</td>
                                        <td>{t.developer_names || '-'}</td>
                                        <td>{tType === 'Bug' ? '—' : (t.bug_count || '0')}</td>
                                        <td>{tType === 'Bug' ? '—' : (t.bug_pending_count || '0')}</td>
                                        <td>{tType === 'Bug' ? '—' : (t.bug_completed_count || '0')}</td>
                                        {companyStatus.map(s => <td key={s}>{t.status_counts?.[s] || '0'}</td>)}
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan={9 + companyStatus.length} className="fd-empty">{search ? `No results for "${search}"` : 'No tickets available'}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="fd-pagination">
                        <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                        <div className="fd-pagination-controls">
                            <button className="fd-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1).map(p => (
                                <button key={p} className={`fd-page-btn ${p === page ? 'fd-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                            ))}
                            <button className="fd-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OverallReport;
